import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Zap,
  MapPin,
  MessageCircle,
  Database,
  BarChart3,
  Mail,
  ArrowRight,
  Check,
  Star,
  Clock,
  Shield,
  Send,
  Users,
  Facebook,
  Instagram,
  Loader2,
  Menu,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button, Badge } from "@/components/kit";
import api, { apiErr } from "@/lib/api";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { ArrowDownUp } from "lucide-react";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay: d },
});

const SOURCES = [
  {
    icon: MapPin,
    name: "Google Maps",
    color: "#10b981",
    desc: "Nama, telepon, website, rating & alamat bisnis.",
  },
  {
    icon: Facebook,
    name: "Facebook",
    color: "#1877F2",
    desc: "Halaman bisnis: kontak, kategori & followers.",
  },
  {
    icon: Instagram,
    name: "Instagram",
    color: "#d62976",
    desc: "Akun bisnis: bio, link, email & nomor WA.",
  },
];

const FEATURES = [
  {
    icon: Database,
    title: "Scrape Multi-Sumber",
    desc: "Google Maps, Facebook & Instagram dalam satu tempat. Cari by keyword, kota & radius — data lengkap.",
  },
  {
    icon: Database,
    title: "Lead Management",
    desc: "Pipeline CRM: tag hot/warm/cold, status New→Closed, import & export CSV/Excel.",
  },
  {
    icon: MessageCircle,
    title: "WA Connector (BYOK)",
    desc: "Hubungkan akun Fonnte milikmu sendiri. Blast dari nomor & kuota kamu — tanpa batas dari kami.",
  },
  {
    icon: Send,
    title: "WA & Email Blast",
    desc: "WA via Fonnte-mu, Email via Gmail-mu (connect sekali klik). Template variabel dinamis & anti-spam.",
  },
  {
    icon: BarChart3,
    title: "Analytics",
    desc: "Funnel konversi, performa blast, top kota & kategori — semua real-time.",
  },
  {
    icon: Shield,
    title: "Anti-Abuse",
    desc: "Quota meter, verifikasi HP, rate limiting. Aman & terkontrol.",
  },
];

const PLANS = [
  {
    name: "Free",
    price: 0,
    yearlyPrice: 0,
    credits: "50",
    sources: "Google Maps",
    blast: false,
    cta: "Mulai Gratis",
    highlight: false,
  },
  {
    name: "Starter",
    price: 99000,
    yearlyPrice: 79200,
    credits: "1.000",
    sources: "+ Facebook & Instagram",
    blast: true,
    cta: "Pilih Starter",
    highlight: false,
  },
  {
    name: "Pro",
    price: 249000,
    yearlyPrice: 199200,
    credits: "3.000",
    sources: "+ Facebook & Instagram",
    blast: true,
    cta: "Pilih Pro",
    highlight: true,
  },
  {
    name: "Agency",
    price: 599000,
    yearlyPrice: 479200,
    credits: "8.000",
    sources: "+ Facebook & Instagram",
    blast: true,
    cta: "Pilih Agency",
    highlight: false,
  },
];

const TESTIMONIALS = [
  {
    name: "Rizky Aditya",
    role: "Freelance Digital Marketer",
    avatar: "RA",
    text: "Dulu spend 3 jam sehari cari leads manual. Sekarang 5 menit udah dapet ratusan data dari Google Maps. Conversion naik 3x sejak pakai RESADA.",
    rating: 5,
  },
  {
    name: "Sari Dewi",
    role: "Agency Owner, Jakarta",
    avatar: "SD",
    text: "Client saya butuh database leads property di Surabaya. RESADA solve dalam hitungan menit. WA blast-nya juga aman, belum pernah kena blokir.",
    rating: 5,
  },
  {
    name: "Budi Santoso",
    role: "UMKM, Yogyakarta",
    avatar: "BS",
    text: "Sebagai UMKM kecil, harga RESADA sangat terjangkau. Paket Free udah cukup buat coba, terus upgrade ke Starter karena hasilnya real.",
    rating: 5,
  },
];

const COMPARISON = [
  { feature: "Scraping Google Maps", resada: true, manual: true, tools: true },
  { feature: "Scraping Facebook & IG", resada: true, manual: false, tools: "Sebagian" },
  { feature: "Lead Management (CRM)", resada: true, manual: false, tools: "Biaya tambahan" },
  { feature: "WA Blast (BYOK)", resada: true, manual: false, tools: "Sebagian" },
  { feature: "Email Blast via Gmail", resada: true, manual: false, tools: false },
  { feature: "Analytics real-time", resada: true, manual: false, tools: "Biaya tambahan" },
  { feature: "Harga per bulan", resada: "Mulai gratis", manual: "Gratis (waktu)", tools: "Rp750rb+" },
  { feature: "Bahasa Indonesia", resada: true, manual: true, tools: false },
];

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [billingYearly, setBillingYearly] = useState(false);
  useEffect(() => {
    api
      .get("/public/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);
  const submitContact = async (e) => {
    e.preventDefault();
    if (contact.name.trim().length < 2 || contact.message.trim().length < 5) {
      toast.error("Lengkapi nama & pesan");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/public/contact", contact);
      toast.success(data.message || "Pesan terkirim!");
      setContact({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSending(false);
    }
  };
  const fmt = (n) => {
    if (n == null) return "—";
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(".0", "") + "jt+";
    if (n >= 1000) return (n / 1000).toFixed(1).replace(".0", "") + "rb+";
    return n.toString();
  };
  return (
    <div className="bg-white text-slate-900 overflow-x-hidden">
      <Seo
        title="RESADA — Scrape Lead Bisnis, CRM & Blast WhatsApp/Email untuk UMKM Indonesia"
        description="Cari ribuan lead bisnis dari Google Maps, Facebook & Instagram, kelola di CRM, perkaya dengan AI, lalu blast WhatsApp & Email otomatis. Mulai gratis hari ini."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "RESADA",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          description:
            "Platform AI untuk scraping leads Google Maps, Facebook & Instagram, CRM, serta blast WhatsApp dan Email untuk bisnis di Indonesia.",
          url: "https://resada.id/",
          image: "https://resada.id/logo.png",
          inLanguage: "id-ID",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "IDR",
            description: "Mulai gratis",
          },
          publisher: {
            "@type": "Organization",
            name: "RESADA",
            logo: {
              "@type": "ImageObject",
              url: "https://resada.id/logo.png",
            },
          },
        }}
      />
      {/* Nav */}
      <header className="sticky top-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo to={null} iconSize={34} />
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#beranda" className="hover:text-sky-600">
              Beranda
            </a>
            <a href="#fitur" className="hover:text-sky-600">
              Fitur
            </a>
            <a href="#harga" className="hover:text-sky-600">
              Harga
            </a>
            <a href="#cara" className="hover:text-sky-600">
              Cara Kerja
            </a>
            <a href="#kontak" className="hover:text-sky-600">
              Kontak
            </a>
            <Link to="/about" className="hover:text-sky-600">
              Tentang
            </Link>
            <Link to="/blog" className="hover:text-sky-600">
              Blog
            </Link>
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                Masuk
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" data-testid="nav-register">
                Daftar Gratis
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-1 md:hidden">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="mobile-login-btn">
                Masuk
              </Button>
            </Link>
            <button
              className="p-2 -mr-2 text-slate-700"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-btn"
              aria-label="Buka menu"
            >
              {menuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div
            className="md:hidden border-t border-slate-100 bg-white/95 backdrop-blur"
            data-testid="mobile-menu-panel"
          >
            <nav className="px-6 py-4 flex flex-col text-sm font-medium text-slate-700">
              {[
                { href: "#beranda", label: "Beranda" },
                { href: "#fitur", label: "Fitur" },
                { href: "#harga", label: "Harga" },
                { href: "#cara", label: "Cara Kerja" },
                { href: "#kontak", label: "Kontak" },
              ].map((m) => (
                <a
                  key={m.href}
                  href={m.href}
                  onClick={() => setMenuOpen(false)}
                  className="py-2.5 hover:text-sky-600"
                >
                  {m.label}
                </a>
              ))}
              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 hover:text-sky-600"
              >
                Tentang
              </Link>
              <Link
                to="/blog"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 hover:text-sky-600"
              >
                Blog
              </Link>
              <div className="flex gap-2 pt-3 mt-2 border-t border-slate-100">
                <Link
                  to="/login"
                  className="flex-1"
                  onClick={() => setMenuOpen(false)}
                >
                  <Button variant="outline" size="sm" className="w-full">
                    Masuk
                  </Button>
                </Link>
                <Link
                  to="/register"
                  className="flex-1"
                  onClick={() => setMenuOpen(false)}
                >
                  <Button
                    size="sm"
                    className="w-full"
                    data-testid="mobile-nav-register"
                  >
                    Daftar Gratis
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="beranda" className="relative">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-20 right-0 w-[500px] h-[500px] bg-sky-200/40 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 relative">
          <motion.div {...fade()} className="max-w-3xl mx-auto text-center">
            <h1 className="font-display text-5xl md:text-6xl font-extrabold leading-[1.05]">
              Cari klien baru <span className="text-sky-500">5 menit</span>,
              bukan 5 jam.
            </h1>
            <p className="text-lg text-slate-600 mt-6 max-w-2xl mx-auto">
              Scrape data bisnis dari Google Maps, kelola lead, lalu blast
              WhatsApp & email — semua dari satu dashboard.{" "}
              <b>1 kredit = 1 lead.</b> Mulai <b>Rp 99.000/bulan</b>.
            </p>
            <div className="flex flex-wrap gap-3 mt-8 justify-center">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta">
                  Coba Gratis Sekarang <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <a href="#cara">
                <Button size="lg" variant="outline">
                  Lihat Cara Kerja
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-slate-500 justify-center">
              <span className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-sky-500" /> Tanpa kartu kredit
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-sky-500" /> Setup 30 detik
              </span>
            </div>
          </motion.div>

          {/* Live counters */}
          <motion.div
            {...fade(0.2)}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16"
            data-testid="landing-stats"
          >
            {[
              {
                n: stats ? fmt(stats.total_users) : "—",
                l: "Pengguna terdaftar",
                i: Users,
              },
              {
                n: stats ? fmt(stats.total_leads) : "—",
                l: "Leads dikumpulkan",
                i: Database,
              },
              {
                n: stats ? fmt(stats.total_messages) : "—",
                l: "Pesan terkirim",
                i: MessageCircle,
              },
              { n: "98%", l: "Tingkat kepuasan", i: Clock },
            ].map((s) => (
              <div
                key={s.l}
                className="card-soft p-5"
                data-testid="landing-stat-card"
              >
                <s.i className="w-6 h-6 text-sky-500 mb-3" />
                <p className="font-display text-3xl font-extrabold">{s.n}</p>
                <p className="text-sm text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Sources */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-4">
        <motion.div {...fade()} className="text-center max-w-2xl mx-auto mb-10">
          <Badge color="sky" className="mb-4">
            3 Sumber Data
          </Badge>
          <h2 className="font-display text-4xl font-extrabold">
            Scrape dari Google Maps, Facebook & Instagram
          </h2>
          <p className="text-slate-600 mt-4">
            Jangkau prospek di mana pun mereka berada — satu platform, tiga
            sumber.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {SOURCES.map((s, i) => (
            <motion.div
              {...fade(i * 0.08)}
              key={s.name}
              className="card-soft p-7 flex items-start gap-4 hover:-translate-y-1 transition-transform"
            >
              <div
                className="w-12 h-12 rounded-2xl grid place-items-center shrink-0"
                style={{ background: `${s.color}1a` }}
              >
                <s.icon className="w-6 h-6" style={{ color: s.color }} />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold">{s.name}</h3>
                <p className="text-slate-600 mt-1 text-sm leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="fitur" className="max-w-7xl mx-auto px-6 py-24">
        <motion.div {...fade()} className="text-center max-w-2xl mx-auto mb-16">
          <Badge color="sky" className="mb-4">
            Fitur Lengkap
          </Badge>
          <h2 className="font-display text-4xl font-extrabold">
            Semua yang kamu butuh untuk outreach
          </h2>
          <p className="text-slate-600 mt-4">
            Dari mencari prospek sampai closing — tanpa pindah-pindah tools.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              {...fade(i * 0.05)}
              key={f.title}
              className="card-soft p-7 hover:-translate-y-1 transition-transform"
            >
              <div className="w-12 h-12 rounded-2xl bg-sky-50 grid place-items-center mb-5">
                <f.icon className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="font-display text-xl font-bold">{f.title}</h3>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                {f.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA after features */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <div className="rounded-2xl bg-sky-50 border border-sky-100 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl font-extrabold text-slate-900">Siap coba RESADA?</h3>
            <p className="text-slate-600 mt-1">Mulai dengan 50 lead gratis — tanpa kartu kredit.</p>
          </div>
          <Link to="/register">
            <Button size="lg">Daftar Gratis <ArrowRight className="w-4 h-4" /></Button>
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <motion.div {...fade()} className="text-center max-w-2xl mx-auto mb-16">
          <Badge color="sky" className="mb-4">
            Testimoni
          </Badge>
          <h2 className="font-display text-4xl font-extrabold">
            Dipercaya oleh bisnis di seluruh Indonesia
          </h2>
          <p className="text-slate-600 mt-4">
            Mereka sudah merasakan dampak RESADA untuk pertumbuhan bisnis mereka.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              {...fade(i * 0.08)}
              key={t.name}
              className="card-soft p-7 hover:-translate-y-1 transition-transform"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(t.rating)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-amber-400" fill="currentColor" />
                ))}
              </div>
              <p className="text-slate-600 text-sm leading-relaxed italic">&ldquo;{t.text}&rdquo;</p>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-slate-100">
                <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-700 font-bold text-sm grid place-items-center">
                  {t.avatar}
                </div>
                <div>
                  <p className="font-semibold text-sm text-slate-800">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section
        id="cara"
        className="bg-slate-900 text-white py-24 relative overflow-hidden"
      >
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge color="sky" className="mb-4">
              3 Langkah
            </Badge>
            <h2 className="font-display text-4xl font-extrabold">
              Dari nol ke ratusan lead
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "Scrape",
                d: "Ketik keyword & pilih kota. Dapatkan daftar bisnis lengkap dalam detik.",
              },
              {
                n: "02",
                t: "Kelola",
                d: "Simpan, tag, dan filter lead di pipeline CRM kamu.",
              },
              {
                n: "03",
                t: "Blast",
                d: "Connect WA, pilih template, kirim pesan personal massal.",
              },
            ].map((s) => (
              <div key={s.n} className="relative">
                <span className="font-display text-6xl font-extrabold text-sky-500/30">
                  {s.n}
                </span>
                <h3 className="font-display text-2xl font-bold mt-2">{s.t}</h3>
                <p className="text-slate-400 mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA after how it works */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-8 text-center">
          <h3 className="font-display text-2xl font-extrabold">3 langkah, hasil nyata.</h3>
          <p className="text-slate-600 mt-2">Ribuan bisnis sudah memulai. Sekarang giliran kamu.</p>
          <Link to="/register" className="inline-block mt-5">
            <Button size="lg">Mulai Gratis Sekarang <ArrowRight className="w-4 h-4" /></Button>
          </Link>
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-5xl mx-auto px-6 py-24">
        <motion.div {...fade()} className="text-center max-w-2xl mx-auto mb-12">
          <Badge color="sky" className="mb-4">
            Perbandingan
          </Badge>
          <h2 className="font-display text-4xl font-extrabold">
            Kenapa RESADA?
          </h2>
          <p className="text-slate-600 mt-4">
            Bandingkan dengan riset manual atau tools lain yang lebih mahal.
          </p>
        </motion.div>
        <div className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left px-6 py-4 font-semibold text-slate-600">Fitur</th>
                  <th className="text-center px-4 py-4 font-bold text-sky-600">RESADA</th>
                  <th className="text-center px-4 py-4 font-semibold text-slate-600">Manual</th>
                  <th className="text-center px-4 py-4 font-semibold text-slate-600">Tools Lain</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.feature} className={i % 2 === 0 ? "bg-slate-50/50" : ""}>
                    <td className="px-6 py-3 font-medium text-slate-700">{row.feature}</td>
                    <td className="text-center px-4 py-3">
                      {row.resada === true ? (
                        <span className="inline-flex w-6 h-6 rounded-full bg-sky-100 text-sky-600 grid place-items-center"><Check className="w-3.5 h-3.5" /></span>
                      ) : (
                        <span className="text-sky-600 font-semibold">{row.resada}</span>
                      )}
                    </td>
                    <td className="text-center px-4 py-3">
                      {row.manual === true ? (
                        <span className="inline-flex w-6 h-6 rounded-full bg-slate-100 text-slate-400 grid place-items-center"><Check className="w-3.5 h-3.5" /></span>
                      ) : (
                        <span className="text-slate-400">{row.manual === false ? "—" : row.manual}</span>
                      )}
                    </td>
                    <td className="text-center px-4 py-3">
                      {row.tools === true ? (
                        <span className="inline-flex w-6 h-6 rounded-full bg-slate-100 text-slate-400 grid place-items-center"><Check className="w-3.5 h-3.5" /></span>
                      ) : (
                        <span className="text-slate-400">{row.tools === false ? "—" : row.tools}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="harga" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge color="sky" className="mb-4">
            Harga Transparan
          </Badge>
          <h2 className="font-display text-4xl font-extrabold">
            Bayar sesuai pemakaian, model kredit
          </h2>
          <p className="text-slate-600 mt-4">
            1 kredit = 1 lead. Kehabisan? Top-up kapan saja (rollover) atau
            aktifkan auto top-up. Hemat 20% dengan paket tahunan.
          </p>
          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-sm font-semibold ${!billingYearly ? "text-sky-600" : "text-slate-400"}`}>Bulanan</span>
            <button
              onClick={() => setBillingYearly(v => !v)}
              className={`relative w-12 h-6 rounded-full transition ${billingYearly ? "bg-sky-500" : "bg-slate-300"}`}
              aria-label="Toggle tahunan"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${billingYearly ? "translate-x-6" : ""}`} />
            </button>
            <span className={`text-sm font-semibold ${billingYearly ? "text-sky-600" : "text-slate-400"}`}>Tahunan <Badge color="green" className="ml-1 text-[10px]">Hemat 20%</Badge></span>
          </div>
          {/* Money-back guarantee */}
          <p className="text-xs text-slate-500 mt-3 flex items-center justify-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-sky-500" /> Garansi uang kembali 7 hari untuk pembelian pertama
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`rounded-2xl p-7 border transition ${p.highlight ? "bg-sky-500 text-white border-sky-500 sky-glow scale-[1.03]" : "bg-white border-slate-200"}`}
            >
              {p.highlight && (
                <Badge color="green" className="mb-3">
                  Paling Populer
                </Badge>
              )}
              <h3 className="font-display text-xl font-bold">{p.name}</h3>
              <p className="mt-3">
                <span className="font-display text-4xl font-extrabold">
                  {p.price === 0 ? "Gratis" : `Rp${billingYearly ? p.yearlyPrice.toLocaleString("id-ID") : p.price.toLocaleString("id-ID")}`}
                </span>
                {p.price > 0 && (
                  <span className={p.highlight ? "text-sky-100" : "text-slate-400"}>
                    /bln
                  </span>
                )}
              </p>
              {billingYearly && p.price > 0 && (
                <p className={`text-xs mt-1 line-through ${p.highlight ? "text-sky-200" : "text-slate-400"}`}>
                  Rp{p.price.toLocaleString("id-ID")}/bln
                </p>
              )}
              <ul
                className={`mt-6 space-y-3 text-sm ${p.highlight ? "text-sky-50" : "text-slate-600"}`}
              >
                <li className="flex gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" /> {p.credits}{" "}
                  kredit scraping/bulan
                </li>
                <li className="flex gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" /> Sumber:{" "}
                  {p.sources}
                </li>
                <li className="flex gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" />{" "}
                  {p.blast
                    ? "WA Blast (BYOK Fonnte) — tanpa batas"
                    : "WA Blast tidak termasuk"}
                </li>
                <li className="flex gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" />{" "}
                  {p.blast
                    ? "Email Blast via Gmail kamu"
                    : "Email Blast tidak termasuk"}
                </li>
              </ul>
              <Link to="/register" className="block mt-7">
                <Button
                  className="w-full"
                  variant={p.highlight ? "dark" : "outline"}
                >
                  {p.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-slate-900 text-white p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-sky-500/30 rounded-full blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-4xl font-extrabold max-w-2xl mx-auto">
              Berhenti mencari prospek manual. Mulai hari ini.
            </h2>
            <Link to="/register" className="inline-block mt-8">
              <Button size="lg" data-testid="footer-cta">
                Daftar Gratis — 50 Leads <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="kontak" className="max-w-5xl mx-auto px-6 pb-24">
        <div className="card-soft p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge color="sky" className="mb-4">
              Hubungi Kami
            </Badge>
            <h2 className="font-display text-3xl font-extrabold">
              Punya pertanyaan?
            </h2>
            <p className="text-slate-600 mt-3">
              Tim kami siap bantu. Kirim pesan dan kami akan menghubungi kamu
              secepatnya.
            </p>
            <div className="mt-6 space-y-2 text-sm text-slate-500">
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-sky-500" /> support@resada.id
              </p>
              <p className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-sky-500" /> Respon &lt;
                24 jam
              </p>
            </div>
          </div>
          <form
            onSubmit={submitContact}
            className="space-y-3"
            data-testid="contact-form"
          >
            <input
              data-testid="contact-name"
              placeholder="Nama kamu"
              value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none"
            />
            <input
              data-testid="contact-email"
              type="email"
              required
              placeholder="Email"
              value={contact.email}
              onChange={(e) =>
                setContact({ ...contact, email: e.target.value })
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none"
            />
            <textarea
              data-testid="contact-message"
              rows={4}
              placeholder="Pesan kamu..."
              value={contact.message}
              onChange={(e) =>
                setContact({ ...contact, message: e.target.value })
              }
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none"
            />
            <Button
              data-testid="contact-submit"
              type="submit"
              loading={sending}
              className="w-full"
            >
              Kirim Pesan <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </section>

      <SiteFooter />
      <FloatingWhatsApp />
    </div>
  );
}
